import isEqual from "lodash/isEqual";
import React, { useEffect, useState } from "react";

import useMedicalCompleteLock from "../../../../hooks/useMedicalCompleteLock";
import MedicalCompleteLockNotice from "../../../global/MedicalCompleteLockNotice";
import { formatDate } from "../../../ui library/helpers/functions";

import AssociateAppointmentsGrid from "./components/AssociateAppointmentsGrid";
import ViewDocument from "./components/ViewDocument";

function PayloadValid(newPayload) {
  return newPayload.every((appointment) => {
    return (
      appointment?.appointment_date &&
      (appointment?.id || appointment?.provider_type) &&
      (appointment?.administered_services || []).every((service) => service?.service)
    );
  });
}

function formatPayload(
  appointments,
  associatedAppointmentsServices,
  associatedAppointmentsIds,
  newAppointments
) {
  return [
    ...appointments.map((appointment) => {
      const updatedServices =
        associatedAppointmentsServices[appointment.id]?.administered_services || [];
      const updatedServiceIds = updatedServices.map((s) => s.id).filter(Boolean);

      const originalServicesFiltered = (appointment?.administered_services || []).filter(
        (service) => !updatedServiceIds.includes(service.id)
      );

      return {
        ...appointment,
        associated: associatedAppointmentsIds.includes(appointment?.id),
        associated_to_other_appt: appointment?.associated_to_other_appt || false,
        administered_services: [
          ...originalServicesFiltered,
          ...updatedServices.map(({ id, service, price }) => ({
            id: id,
            appointment: appointment.id,
            service,
            price: Number(price || 0),
          })),
        ],
      };
    }),
    ...newAppointments.map((appointment) => {
      const {
        appointment_date,
        appointment_time,
        provider_type,
        associated,
        administered_services,
      } = appointment;

      const parsedApptDate = formatDate(new Date(appointment_date).toISOString(), "yyyy-MM-dd");

      return {
        appointment_date: parsedApptDate,
        appointment_time,
        provider_type,
        associated,
        associated_to_other_appt: false,
        administered_services: [
          ...(administered_services || []).map((service) => ({
            service: service.service || service.name,
            price: Number(service.price || service.amount || 0),
          })),
        ],
      };
    }),
  ];
}

function AssociateAppointments({
  appointments,
  schema,
  readOnly,
  title,
  questionKey,
  handleAnswerChange,
}) {
  const { document_url: documentUrl } = readOnly;
  const { canEditMedicallyCompleteCase } = useMedicalCompleteLock();
  const locked = Boolean(readOnly?.medical_status_locked) && !canEditMedicallyCompleteCase;
  const [associatedAppointmentsIds, setAssociatedAppointmentsIds] = useState([]);
  const [associatedAppointmentsServices, setAssociatedAppointmentsServices] = useState({});
  const [newAppointments, setNewAppointments] = useState([]);
  const [payload, setPayload] = useState([]);
  const [initialPayload, setInitialPayload] = useState([]);

  useEffect(() => {
    const updatedAssociatedAppointmentsIds = appointments
      .filter((appointment) => appointment.associated)
      .map((appointment) => appointment.id);

    setAssociatedAppointmentsIds(updatedAssociatedAppointmentsIds);
    const administeredServicesByAppointment = Object.fromEntries(
      appointments.map((appt) => [appt.id, { administered_services: appt.administered_services }])
    );
    setAssociatedAppointmentsServices(administeredServicesByAppointment);

    setInitialPayload(
      formatPayload(
        appointments,
        administeredServicesByAppointment,
        updatedAssociatedAppointmentsIds,
        []
      )
    );
  }, [appointments]);

  useEffect(() => {
    const newPayload = formatPayload(
      appointments,
      associatedAppointmentsServices,
      associatedAppointmentsIds,
      newAppointments
    );

    if (newPayload.length === 0) {
      return;
    }

    if (!isEqual(newPayload, initialPayload) && PayloadValid(newPayload)) {
      setPayload(newPayload);
      handleAnswerChange(questionKey, newPayload);
    } else {
      handleAnswerChange(questionKey, appointments);
    }
  }, [appointments, newAppointments, associatedAppointmentsIds, associatedAppointmentsServices, handleAnswerChange, questionKey]);
  return (
    <div className="flex w-full">
      <div className="h-[70vh] w-[45%]">
        <ViewDocument documentUrl={documentUrl} />
      </div>
      <div className="grow">
        <MedicalCompleteLockNotice locked={locked} className="mb-4" />
        <AssociateAppointmentsGrid
          title={title}
          locked={locked}
          schema={schema}
          appointments={appointments}
          associatedAppointmentsServices={associatedAppointmentsServices}
          setAssociatedAppointmentsServices={setAssociatedAppointmentsServices}
          associatedAppointmentsIds={associatedAppointmentsIds}
          setAssociatedAppointmentsIds={setAssociatedAppointmentsIds}
          newAppointments={newAppointments}
          setNewAppointments={setNewAppointments}
        />
      </div>
    </div>
  );
}

export default AssociateAppointments;
